import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Shield, Monitor, Bug, FileSearch, Lock, Wifi } from 'lucide-react';
import { createClient, onboardClient } from '../lib/api';

const SERVICES = [
  { id: 'fleetdm', icon: Monitor, label: 'FleetDM' },
  { id: 'wazuh', icon: Shield, label: 'Wazuh' },
  { id: 'secuscan', icon: Bug, label: 'SecuScan' },
  { id: 'vciso', icon: FileSearch, label: 'vCISO' },
  { id: 'vaultwarden', icon: Lock, label: 'Vaultwarden' },
  { id: 'rustdesk', icon: Wifi, label: 'RustDesk' },
];

const INDUSTRIES = [
  'Technology', 'Finance', 'Healthcare', 'Legal', 'Manufacturing', 'Education', 'Government', 'Retail', 'Other',
];

type UserEntry = { email: string; first_name: string; last_name: string; role: string };

export default function ClientCreatePage() {
  const [step, setStep] = useState(1);
  const [company, setCompany] = useState({ name: '', industry: '', contact_email: '' });
  const [users, setUsers] = useState<UserEntry[]>([{ email: '', first_name: '', last_name: '', role: 'viewer' }]);
  const [services, setServices] = useState<Record<string, boolean>>({ fleetdm: true, wazuh: true });
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [onboardingTasks, setOnboardingTasks] = useState<any[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const addUser = () => setUsers([...users, { email: '', first_name: '', last_name: '', role: 'viewer' }]);
  const removeUser = (i: number) => setUsers(users.filter((_, idx) => idx !== i));
  const updateUser = (i: number, field: keyof UserEntry, val: string) => {
    const copy = [...users];
    copy[i] = { ...copy[i], [field]: val };
    setUsers(copy);
  };

  const toggleService = (id: string) => setServices((s) => ({ ...s, [id]: !s[id] }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const validUsers = users.filter((u) => u.email);
      const client = await createClient({
        name: company.name,
        industry: company.industry,
        contact_email: company.contact_email,
        services_json: JSON.stringify(services),
        users: validUsers,
      });
      setCreatedId(client.id);
      toast.success(t('clients.created'));

      // Start onboarding.
      await onboardClient(client.id);
      setStep(5);

      // Poll onboarding status.
      const poll = setInterval(async () => {
        try {
          const { fetchClientOnboarding } = await import('../lib/api');
          const status = await fetchClientOnboarding(client.id);
          setOnboardingTasks(status.tasks || []);
          if (status.overall === 'completed' || status.overall === 'failed') {
            clearInterval(poll);
          }
        } catch {
          clearInterval(poll);
        }
      }, 2000);
    } catch (err: any) {
      toast.error(err.message || t('clients.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 1) return company.name.length > 0;
    if (step === 2) return users.some((u) => u.email);
    if (step === 3) return Object.values(services).some(Boolean);
    return true;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate('/clients')} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
        <ArrowLeft className="w-4 h-4" /> {t('clients.backToClients')}
      </button>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('clients.newClient')}</h1>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className={`flex items-center gap-2 ${s <= step ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
              s < step ? 'bg-blue-600 text-white border-blue-600' :
              s === step ? 'border-blue-600 dark:border-blue-400' :
              'border-gray-200 dark:border-gray-700'
            }`}>
              {s < step ? <Check className="w-4 h-4" /> : s}
            </div>
            {s < 5 && <div className={`w-8 h-0.5 ${s < step ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Company */}
      {step === 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('clients.stepCompany')}</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('clients.companyName')} *</label>
              <input type="text" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('clients.industry')}</label>
              <select value={company.industry} onChange={(e) => setCompany({ ...company, industry: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
                <option value="">-</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('clients.contactEmail')}</label>
              <input type="email" value={company.contact_email} onChange={(e) => setCompany({ ...company, contact_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100" />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Users */}
      {step === 2 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('clients.stepUsers')}</h2>
          {users.map((u, i) => (
            <div key={i} className="grid grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('clients.email')} *</label>
                <input type="email" value={u.email} onChange={(e) => updateUser(i, 'email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('clients.firstName')}</label>
                <input type="text" value={u.first_name} onChange={(e) => updateUser(i, 'first_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('clients.lastName')}</label>
                <input type="text" value={u.last_name} onChange={(e) => updateUser(i, 'last_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm" />
              </div>
              <div className="flex gap-2">
                <select value={u.role} onChange={(e) => updateUser(i, 'role', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm">
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
                {users.length > 1 && (
                  <button onClick={() => removeUser(i)} className="p-2 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
          <button onClick={addUser} className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline">
            <Plus className="w-4 h-4" /> {t('clients.addUser')}
          </button>
        </div>
      )}

      {/* Step 3: Services */}
      {step === 3 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('clients.stepServices')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SERVICES.map((svc) => {
              const Icon = svc.icon;
              const active = services[svc.id];
              return (
                <button key={svc.id} onClick={() => toggleService(svc.id)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    active ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}>
                  <Icon className={`w-6 h-6 mb-2 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>{svc.label}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 4: Summary */}
      {step === 4 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('clients.stepSummary')}</h2>
          <div className="space-y-3 text-sm">
            <div><span className="text-gray-500 dark:text-gray-400">{t('clients.companyName')}:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{company.name}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('clients.industry')}:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{company.industry || '-'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('clients.contactEmail')}:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{company.contact_email || '-'}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('clients.users')}:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{users.filter((u) => u.email).length}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('clients.services')}:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{Object.entries(services).filter(([, v]) => v).map(([k]) => k).join(', ')}</span></div>
          </div>
        </div>
      )}

      {/* Step 5: Deployment */}
      {step === 5 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('clients.stepDeployment')}</h2>
          <div className="space-y-2">
            {onboardingTasks.map((task: any, i: number) => (
              <div key={i} className="flex items-center gap-3 py-2">
                {task.status === 'completed' && <Check className="w-5 h-5 text-green-500" />}
                {task.status === 'running' && <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
                {task.status === 'failed' && <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs">!</div>}
                {task.status === 'pending' && <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />}
                <span className={`text-sm ${task.status === 'completed' ? 'text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                  {task.step.replace(/_/g, ' ')}
                </span>
                {task.message && task.status === 'failed' && (
                  <span className="text-xs text-red-500 ml-auto">{task.message}</span>
                )}
              </div>
            ))}
            {onboardingTasks.length === 0 && <p className="text-sm text-gray-500">{t('clients.onboardingStarting')}</p>}
          </div>
          {onboardingTasks.every((t: any) => t.status === 'completed') && onboardingTasks.length > 0 && (
            <button onClick={() => navigate(`/clients/${createdId}`)} className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
              {t('clients.viewClient')}
            </button>
          )}
        </div>
      )}

      {/* Navigation */}
      {step < 5 && (
        <div className="flex justify-between">
          <button onClick={() => step > 1 && setStep(step - 1)} disabled={step === 1}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 disabled:opacity-30">
            <ArrowLeft className="w-4 h-4" /> {t('common.previous')}
          </button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canNext()}
              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
              {t('common.next')} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-1 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50">
              {submitting ? t('common.running') : t('clients.deploy')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
